import { STRIPE_CARD_BRAND_FALLBACK_LABEL, STRIPE_CARD_BRAND_LABELS } from '@/lib/constants/stripe';
import { CARD_ENTRY_ERROR_CODES } from '@/lib/types/Billing';
import type { IBillingAccountViewData, ICardBillingDetails, ICardOnFileRes, ICardSummary } from '@/lib/types/Billing';
import { ApiError } from '@/shared/api/ApiError';
import type { StripeBillingDetails, StripeError } from '@/shared/stripe/stripeTypes';
import type { CardBillingFormValues } from './schemas/billingSchemas';

/** Stripe.js error codes that mean the SetupIntent can no longer be confirmed: mint a new one. */
const NEW_SETUP_INTENT_CODES: readonly string[] = ['setup_intent_unexpected_state', 'resource_missing'];

const GENERIC_STRIPE_ERROR = 'We could not verify this card right now. Please try again in a moment.';
const GENERIC_SETUP_ERROR = "We couldn't start a secure card session. Check your connection and try again.";

/** Pure helpers for the embedded Stripe card entry flow (tests colocated). */
export class CardEntryUtils {
  /**
   * Card on file from GET billing/card. Nothing on file arrives as `{}` — the platform
   * printer drops null fields, so the `card` key is absent, not null (contract §2.3).
   */
  static cardFromRes(res: ICardOnFileRes | undefined): ICardSummary | null {
    return res?.card ?? null;
  }

  /** Customer-facing brand label for a Stripe `card.brand` id. */
  static brandLabel(brand: string): string {
    return STRIPE_CARD_BRAND_LABELS[brand.toLowerCase()] ?? STRIPE_CARD_BRAND_FALLBACK_LABEL;
  }

  /** `MM/YYYY` — the form every issuer prints on the card. */
  static formatExpiry(expMonth: number, expYear: number): string {
    return `${String(expMonth).padStart(2, '0')}/${expYear}`;
  }

  /** "Visa •••• 4242" */
  static cardSummaryLabel(card: ICardSummary): string {
    return `${CardEntryUtils.brandLabel(card.brand)} •••• ${card.last4}`;
  }

  /**
   * A card is valid through the last day of its expiry month. `now` is injected
   * (year + 1-indexed month) so the check is deterministic.
   */
  static isExpired(card: ICardSummary, now: { year: number; month: number }): boolean {
    return card.expYear < now.year || (card.expYear === now.year && card.expMonth < now.month);
  }

  /** Form defaults from the billing account on file (empty strings when none). */
  static billingDefaults(paymentMethod: IBillingAccountViewData | null): CardBillingFormValues {
    return {
      fullName: paymentMethod?.fullName ?? '',
      orgName: paymentMethod?.orgName ?? '',
      address1: paymentMethod?.address1 ?? '',
      city: paymentMethod?.city ?? '',
      region: paymentMethod?.region ?? '',
      postalCode: paymentMethod?.postalCode ?? '',
      countryIso2: paymentMethod?.countryIso2 ?? 'US',
    };
  }

  /** Wire shape for the confirm endpoint: empty organization becomes null. */
  static toCardBillingDetails(values: CardBillingFormValues): ICardBillingDetails {
    return {
      fullName: values.fullName.trim(),
      orgName: values.orgName.trim() || null,
      address1: values.address1.trim(),
      city: values.city.trim(),
      region: values.region.trim(),
      postalCode: values.postalCode.trim(),
      countryIso2: values.countryIso2,
    };
  }

  /**
   * `payment_method_data.billing_details` for confirmSetup. Required in full because
   * the Element collects no billing fields (fields.billingDetails = 'never'); AVS and
   * Radar see the same address the platform stores.
   */
  static toStripeBillingDetails(values: CardBillingFormValues): StripeBillingDetails {
    return {
      name: values.fullName.trim(),
      address: {
        line1: values.address1.trim(),
        city: values.city.trim(),
        state: values.region.trim(),
        postal_code: values.postalCode.trim(),
        country: values.countryIso2,
      },
    };
  }

  /**
   * Inline message for a failed confirmSetup. Card and validation errors carry
   * Stripe's customer-safe text (issuer decline reasons, "incomplete number");
   * everything else is a transient/developer condition and gets one generic line.
   */
  static stripeErrorMessage(error: StripeError): string {
    if ((error.type === 'card_error' || error.type === 'validation_error') && error.message) {
      return error.message;
    }
    return GENERIC_STRIPE_ERROR;
  }

  /** True when Stripe says the SetupIntent was consumed/expired and a fresh one must be minted. */
  static needsNewSetupIntent(error: StripeError): boolean {
    return error.code !== undefined && NEW_SETUP_INTENT_CODES.includes(error.code);
  }

  /** Machine code from a platform VError envelope (`data.code`), if the error is one. */
  static apiErrorCode(error: unknown): string | null {
    return error instanceof ApiError ? error.data?.code ?? null : null;
  }

  /** Inline message when POST billing/card/setup fails (contract §2.1 codes). */
  static setupErrorMessage(error: unknown): string {
    switch (CardEntryUtils.apiErrorCode(error)) {
      case CARD_ENTRY_ERROR_CODES.DISABLED:
        return 'Card updates are temporarily unavailable. Please try again later.';
      case CARD_ENTRY_ERROR_CODES.NO_BILLING_CLIENT:
        return 'This account has no billing profile yet. Please contact billing support.';
      case CARD_ENTRY_ERROR_CODES.STRIPE_UNAVAILABLE:
        return 'Our card processor is temporarily unreachable. Please try again in a moment.';
      default:
        return GENERIC_SETUP_ERROR;
    }
  }

  /**
   * True when POST billing/card/confirm rejected the intent for good (belongs to
   * another customer, or canceled): the only recovery is a fresh setup + re-entry.
   */
  static needsNewSetupAfterConfirmError(error: unknown): boolean {
    const code = CardEntryUtils.apiErrorCode(error);
    if (code === CARD_ENTRY_ERROR_CODES.SETUP_INTENT_MISMATCH) return true;
    return (
      code === CARD_ENTRY_ERROR_CODES.SETUP_INTENT_NOT_SUCCEEDED &&
      error instanceof ApiError &&
      error.data?.status === 'canceled'
    );
  }

  /** Inline message when POST billing/card/confirm fails after Stripe verified the card. */
  static confirmErrorMessage(error: unknown, retryLabel: string): string {
    switch (CardEntryUtils.apiErrorCode(error)) {
      case CARD_ENTRY_ERROR_CODES.SETUP_INTENT_NOT_SUCCEEDED:
        return 'The card could not be verified. Please check the details and try again.';
      case CARD_ENTRY_ERROR_CODES.SETUP_INTENT_MISMATCH:
        return 'This card session no longer matches your account. A fresh secure form has loaded — please enter the card again.';
      case CARD_ENTRY_ERROR_CODES.DISABLED:
        return 'Card updates are temporarily unavailable. Please try again later.';
      case CARD_ENTRY_ERROR_CODES.STRIPE_UNAVAILABLE:
        return `Your card was verified but our card processor did not answer. Click ${retryLabel} to try again.`;
      default: {
        const detail = error instanceof Error && error.message ? ` (${error.message})` : '';
        return `Your card was verified but could not be saved to your account${detail}. Click ${retryLabel} to try again.`;
      }
    }
  }
}
